import * as React from 'react'
import { Stage, Layer, Image as KImage, Circle, Line, Wedge, Text, Group, Rect } from 'react-konva'
import type Konva from 'konva'
import {
  ChartScanActivity,
  ChartScanDisc,
  ChartScanDistanceData,
  ActivityType,
} from '@/lib/api/company/chartScan'
import {
  MINUTES_PER_DAY,
  isoToMinute,
  minuteToAngleRad,
  parseChartIso,
  toChartIso,
} from './discPolar'
import { ActivityBoundaryHandles } from './ActivityBoundaryHandles'

type Props = {
  imageUrl: string
  disc: ChartScanDisc
  activities: ChartScanActivity[]
  onActivitiesChange: (next: ChartScanActivity[], sameDrag: boolean) => void
  // External hover/select state for table↔canvas sync.
  hoveredActivityId?: number | null
  selectedActivityIds?: Set<number>
  onHoverActivity?: (id: number | null, fromCanvas: boolean) => void
  onSelectActivity?: (ids: number[], shift: boolean, ctrl: boolean) => void
  // Right-click on the disc. `activity` is the wedge under the cursor, or null
  // when the click landed on empty disc area / a boundary handle. `minute` is
  // the chart minute (0..1440) at the click angle — used by the "Ustaw początek
  // / koniec dnia" menu items to set a boundary to the right-clicked position
  // (1:1 with WPF's SetDayStartTime / SetDayEndTime context-menu commands).
  onContextMenuActivity?: (
    activity: ChartScanActivity | null,
    screenXY: { x: number; y: number },
    minute: number,
  ) => void
  // Boundaries (drag handles on outer ring).
  startBoundaryMinute?: number | null
  endBoundaryMinute?: number | null
  noonShiftMinutes?: number
  // When set, activities whose minute lies strictly outside [startMin, endMin]
  // render greyed (matches WPF IsOutOfDay-driven palette).
  outOfDayGrey?: boolean
  onChangeStartBoundary?: (minute: number) => void
  onChangeEndBoundary?: (minute: number) => void
  onChangeNoonShift?: (minute: number) => void
  // Cursor time read-out.
  onCursorTimeChange?: (minute: number | null) => void
  // Day-relative noon shift, used by hover tooltip to show "shifted" times the
  // same way the activity table does (matches WPF ShiftedStartTime).
  // (already passed via noonShiftMinutes above; just forwarded into tooltip.)
  // Click-on-canvas in adding mode → fires with the clicked minute (only when
  // the click landed outside an existing wedge).
  onClickEmptyArea?: (minute: number) => void
  // Phase 3 — speed-trace ring + per-driving-tick distance points. When showDistance
  // is true, the canvas draws a thin outline of the speed-trace circle and small red
  // dots at each detected distance-tick. Mirrors WPF's IsDistancePointsVisible toggle.
  distanceData?: ChartScanDistanceData | null
  showDistance?: boolean
  // Distance-point editing — 1:1 with WPF Add/RemoveDistanceStartPoint
  // (TachographChartControl.OnPreviewMouseLeftButtonDown). Active only while
  // showDistance is on. Shift+left-click on the disc snaps the click onto the
  // nearest distance ring and adds a point there; left-clicking an existing dot
  // removes it. When these are omitted the dots stay read-only.
  onAddDistancePoint?: (kind: 'start' | 'end', point: { x: number; y: number }) => void
  onRemoveDistancePoint?: (kind: 'start' | 'end', index: number) => void
  // Vehicle-change minutes (one entry per change instant, day-relative). Each one
  // renders as a thick white radial line from center to rim — matches WPF's
  // TachographChartVehicleChange template.
  beginCountryCode?: string
  endCountryCode?: string
  vehicleChangeMinutes?: number[]
  // Country-change markers: purple radial + ISO code label outside the rim.
  countryChanges?: { minute: number; code: string }[]
  width?: number
  height?: number
  zoom?: number
  onZoomChange?: (zoom: number) => void
  // 1:1 with WPF ShowNotClippedTachographChart configuration toggle
  // (TachographChartImportViewModel.RotateImage:637): when ON, the chart
  // image renders at ~55% of the canvas radius while boundary handles +
  // hour labels stay at the outer rim, leaving generous padding around
  // the disc. When OFF, the chart fills the canvas (compact view).
  compactView?: boolean
}

// Server-side canonical chart is 800x800 with the teardrop at (400, 400) and the
// chart's outer ring (MinimalTachographChartRadius) at 400 px from center.
const CANONICAL_SIZE = 800
const CANONICAL_CENTER = 400
const CANONICAL_OUTER_RADIUS = 400
// 1:1 with WPF TachographChartControl.CoerceTachographChartImage:
//   newImage.Cut(new Rectangle(0, 0, W, H), 40)
// shrinks the image inside its canvas by a 40-px white border on each side
// (so the displayed disc has 360 / 400 = 90% of the wedge radius). The
// wedges still draw at full radius — that produces the saturated colored
// "halo" ring at the rim where the chart background is white. Without this,
// wedges and image share the same extent and the chart content gets obscured.
const CANONICAL_BORDER = 40
const IMAGE_SCALE_RATIO = (CANONICAL_OUTER_RADIUS - CANONICAL_BORDER) / CANONICAL_OUTER_RADIUS

// Activity colors from WPF DriverActivityTypeToColorConverter.cs (verbatim).
const ACTIVITY_COLOR: Record<number, string> = {
  [ActivityType.Rest]: 'rgb(117, 197, 240)',
  [ActivityType.Availability]: 'rgb(255, 245, 0)',
  [ActivityType.Work]: 'rgb(231, 120, 23)',
  [ActivityType.Driving]: 'rgb(218, 37, 29)',
  [ActivityType.NoData]: 'rgb(220, 220, 220)',
}

// Grayscale palette for activities that fall OUTSIDE the day boundaries
// (between dayEnd and dayStart). 1:1 with WPF
// SuperTacho.DataModel/Helpers/ActivityTypeHelper.GetGrayScaleColor.
// WPF re-colors out-of-day wedges with a distinct gray *per activity type*
// rather than just dimming opacity, so the chart still distinguishes "out
// of day Work" from "out of day Rest" while making them visually recede.
const ACTIVITY_COLOR_GRAY: Record<number, string> = {
  [ActivityType.Rest]: 'rgb(177, 177, 177)',
  [ActivityType.Availability]: 'rgb(220, 220, 220)',
  [ActivityType.Work]: 'rgb(142, 142, 142)',
  [ActivityType.Driving]: 'rgb(90, 90, 90)',
  [ActivityType.NoData]: 'rgb(190, 190, 190)',
}

const ACTIVITY_LABEL: Record<number, string> = {
  [ActivityType.Rest]: 'Odpoczynek',
  [ActivityType.Availability]: 'Dyspozycja',
  [ActivityType.Work]: 'Praca',
  [ActivityType.Driving]: 'Jazda',
  [ActivityType.NoData]: 'Brak danych',
}

// WPF zoom range (TachographChartControl.cs:55-94): factor in [1.0, 5.0].
const ZOOM_MIN = 1.0
const ZOOM_MAX = 5.0
const WHEEL_STEP = 0.0005

// How close (in canonical 800-space units) a shift+click must land to a distance
// ring before it's accepted as "add a point on that ring". WPF uses a ±5 band
// per ring (TachographChartControl.IsPointInCircleClicked); we widen it slightly
// so the gesture is comfortable while still ignoring clicks far from the rings.
const DISTANCE_RING_SNAP_TOLERANCE = 12

export function TachographChartCanvas({
  imageUrl,
  disc,
  activities,
  onActivitiesChange,
  hoveredActivityId = null,
  selectedActivityIds,
  onHoverActivity,
  onSelectActivity,
  onContextMenuActivity,
  startBoundaryMinute = null,
  endBoundaryMinute = null,
  noonShiftMinutes = 0,
  outOfDayGrey = true,
  onChangeStartBoundary,
  onChangeEndBoundary,
  onChangeNoonShift,
  onCursorTimeChange,
  onClickEmptyArea,
  distanceData = null,
  showDistance = false,
  onAddDistancePoint,
  onRemoveDistancePoint,
  beginCountryCode,
  endCountryCode,
  vehicleChangeMinutes = [],
  countryChanges = [],
  width = 700,
  height = 700,
  zoom = 1,
  onZoomChange,
  compactView = false,
}: Props) {
  const [img, setImg] = React.useState<HTMLImageElement | null>(null)
  React.useEffect(() => {
    if (!imageUrl) return
    const el = new window.Image()
    el.onload = () => setImg(el)
    el.src = imageUrl
  }, [imageUrl])

  // Track screen-pixel cursor position so the hover tooltip can follow the mouse
  // (Konva has no native tooltip; we overlay an absolutely-positioned HTML node).
  const [tooltipPos, setTooltipPos] = React.useState<{ x: number; y: number } | null>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  // Canvas-pixel center + outer radius (before stage zoom is applied). Reserve
  // ~12% of the half-dimension for hour labels + spokes outside the disc, so
  // the rim doesn't clip against the canvas edge (matches WPF: hour numbers sit
  // OUTSIDE the disc but INSIDE the chart canvas frame).
  const cx = width / 2
  const cy = height / 2
  const outerR = Math.min(width, height) / 2 - 28
  // Scale that maps canonical (400, 400) → canvas (cx, cy) and canonical 400 → outerR.
  // Image scaling depends on the view mode:
  //   - Wide view (WPF default, compactView=false): chart at ~55% of canvas
  //     radius — matches WPF's CutCircle(1450, 1450) when ShowNotClipped is ON.
  //     Boundary handles + hour labels stay at outerR, leaving wide padding.
  //   - Compact view (compactView=true): chart at 90% of canvas radius (the
  //     borderSize=40 shrinkage); the disc fills the available space.
  const imageScale = compactView
    ? (outerR * IMAGE_SCALE_RATIO) / CANONICAL_OUTER_RADIUS
    : (outerR * 0.55) / CANONICAL_OUTER_RADIUS
  const imageDrawW = CANONICAL_SIZE * imageScale
  const imageDrawH = CANONICAL_SIZE * imageScale
  const imageX = cx - imageDrawW / 2
  const imageY = cy - imageDrawH / 2

  // Stage-level pan offset (ranges 0..(width*(zoom-1))).
  const [panX, setPanX] = React.useState(0)
  const [panY, setPanY] = React.useState(0)
  React.useEffect(() => {
    if (zoom <= 1) {
      setPanX(0)
      setPanY(0)
    }
  }, [zoom])

  const dayStartIso = disc.dayStartDate
  // 1:1 with WPF: the chart's 12 o'clock corresponds to MIDNIGHT (00:00) of
  // the disc's calendar day, not to the dayStart boundary. dayStartIso is
  // used only for the boundary handle position and form-field round-tripping.
  // The angular minute axis is anchored at midnight so 04:23 sits at ~67°
  // (between hours 4 and 5 on the chart face), matching WPF.
  const chartAnchorIso = React.useMemo(() => {
    const d = parseChartIso(dayStartIso)
    return toChartIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())))
  }, [dayStartIso])

  // 1:1 with WPF: activities are positioned by REAL time (TachographChart
  // Activity.RefreshActivityStartPoint uses _startTime directly, not
  // ShiftedStartTime). The hour LABELS rotate by -noonShift instead — see
  // TachographChartHour.RefreshHourPoint:151. Net visual effect when the user
  // drags the noon-shift handle: the time-marking ring rotates around the
  // stationary activity wedges, so the user can re-align "their noon" with
  // the chart's 12 o'clock without disturbing the recorded data.
  const minuteActivities = React.useMemo(() => {
    const wrap = (m: number) => ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
    return activities
      .map((a) => ({
        type: a.type,
        startMin: wrap(isoToMinute(chartAnchorIso, a.startTime)),
        endMin: wrap(isoToMinute(chartAnchorIso, a.endTime)),
        manualEntry: a.manualEntry,
        ferryTrainCrossing: a.ferryTrainCrossing,
        crew: a.crew,
        activityId: a.activityId,
      }))
      .sort((a, b) => a.startMin - b.startMin)
  }, [activities, chartAnchorIso])

  // Convert a stage-coord pointer position into the chart's polar minute (0..1440).
  const pointerToMinute = (sx: number, sy: number): number => {
    // Account for stage-level zoom + pan.
    const localX = (sx - panX) / zoom
    const localY = (sy - panY) / zoom
    const dx = localX - cx
    const dy = localY - cy
    const angle = Math.atan2(dy, dx)
    let m = ((angle - Math.PI) * MINUTES_PER_DAY) / (2 * Math.PI)
    m = ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
    return m
  }

  const handleStageWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault()
    if (!onZoomChange) return
    const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom + WHEEL_STEP * -e.evt.deltaY))
    if (next === zoom) return
    // Anchor zoom on the cursor position (matches WPF's zoom-around-cursor
    // behaviour). Without this the chart always zooms toward (0, 0) and the
    // user has to manually pan back to where they were looking.
    const stage = e.target.getStage()
    const pointer = stage?.getPointerPosition()
    if (pointer) {
      // Cursor in chart-space (pre-zoom): (pointer - currentPan) / currentZoom.
      // We want the same chart-space point under the cursor at the new zoom,
      // which means: newPan = pointer - chartSpacePoint * newZoom.
      const chartX = (pointer.x - panX) / zoom
      const chartY = (pointer.y - panY) / zoom
      setPanX(pointer.x - chartX * next)
      setPanY(pointer.y - chartY * next)
    }
    onZoomChange(next)
  }

  const handleStageMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage()
    if (!stage) return
    const pos = stage.getPointerPosition()
    if (!pos) return
    onCursorTimeChange?.(Math.round(pointerToMinute(pos.x, pos.y)))
    setTooltipPos({ x: pos.x, y: pos.y })
  }
  const handleStageMouseLeave = () => {
    onCursorTimeChange?.(null)
    setTooltipPos(null)
  }

  // Konva fires mouseover/mouseout on every shape. We promote those into a CSS
  // cursor change on the wrapper so the user can tell when something is grabbable
  // (WPF uses Cursors.Hand).
  const setStageCursor = (cursor: string) => {
    const el = wrapperRef.current?.querySelector('canvas') as HTMLCanvasElement | null
    if (el) el.style.cursor = cursor
  }

  // Convert a stage-coord pointer position into a canonical (800-space) point.
  // Inverse of the forward transform used to draw everything:
  //   screen = (cx | cy) + (canonical - CANONICAL_CENTER) * imageScale, then the
  //   Stage applies its own scale (zoom) + position (pan) on top.
  const pointerToCanonical = (sx: number, sy: number): { x: number; y: number } => {
    const localX = (sx - panX) / zoom
    const localY = (sy - panY) / zoom
    return {
      x: CANONICAL_CENTER + (localX - cx) / imageScale,
      y: CANONICAL_CENTER + (localY - cy) / imageScale,
    }
  }

  // Shift+click in distance mode → snap the click onto the nearest distance ring
  // and add a point. 1:1 with WPF AddDistanceStartPoint/AddDistanceEndPoint: the
  // angle comes from the click, the radius is fixed to the ring. Returns true if
  // a point was added (so the caller skips the normal click handling).
  const tryAddDistancePoint = (sx: number, sy: number): boolean => {
    if (!showDistance || !onAddDistancePoint || !distanceData) return false
    const c = pointerToCanonical(sx, sy)
    const dx = c.x - distanceData.circleCenterX
    const dy = c.y - distanceData.circleCenterY
    const r = Math.hypot(dx, dy)
    if (r === 0) return false
    const dStart = Math.abs(r - distanceData.startPointsRadius)
    const dEnd = Math.abs(r - distanceData.endPointsRadius)
    const kind: 'start' | 'end' = dStart <= dEnd ? 'start' : 'end'
    const nearest = Math.min(dStart, dEnd)
    if (nearest > DISTANCE_RING_SNAP_TOLERANCE) return false
    const ring = kind === 'start' ? distanceData.startPointsRadius : distanceData.endPointsRadius
    onAddDistancePoint(kind, {
      x: distanceData.circleCenterX + (dx / r) * ring,
      y: distanceData.circleCenterY + (dy / r) * ring,
    })
    return true
  }

  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    // Only fire when the click was on the Stage background or the disc Image, NOT
    // on a wedge, boundary handle, or distance dot (those have their own handlers
    // and stop here naturally when they call e.cancelBubble).
    if (e.target !== e.currentTarget && e.target.className !== 'Image') return
    const stage = e.target.getStage()
    if (!stage) return
    const pos = stage.getPointerPosition()
    if (!pos) return
    // Distance-point add takes priority while the shift key is held in distance mode.
    if (e.evt.shiftKey && tryAddDistancePoint(pos.x, pos.y)) return
    if (!onClickEmptyArea) return
    onClickEmptyArea(Math.round(pointerToMinute(pos.x, pos.y)))
  }

  // Right-click on empty disc area (or a boundary handle / marker line). Wedges
  // handle their own contextmenu and stop propagation, so this only fires when
  // the click did NOT land on an activity wedge — exactly where WPF still offers
  // the "Ustaw początek/koniec dnia" set-to-cursor commands.
  const handleStageContextMenu = (e: Konva.KonvaEventObject<PointerEvent>) => {
    e.evt.preventDefault()
    if (!onContextMenuActivity) return
    const stage = e.target.getStage()
    const pos = stage?.getPointerPosition()
    if (!pos) return
    onContextMenuActivity(
      null,
      { x: e.evt.clientX, y: e.evt.clientY },
      Math.round(pointerToMinute(pos.x, pos.y)),
    )
  }

  // Resolve the hovered activity (in canonical coords) for the tooltip.
  const hoveredActivity = React.useMemo(
    () => activities.find((a) => a.activityId === hoveredActivityId) ?? null,
    [activities, hoveredActivityId],
  )

  return (
    <div ref={wrapperRef} className="tw-relative" style={{ width, height }}>
    <Stage
      width={width}
      height={height}
      scaleX={zoom}
      scaleY={zoom}
      x={panX}
      y={panY}
      draggable={zoom > 1}
      onDragEnd={(e) => {
        // Konva fires dragend events that bubble from any child node up to the
        // Stage. If the user just released a handle Circle (boundary, junction,
        // noon-shift, vehicle/country marker), `e.target` is the Circle —
        // reading its x()/y() would set pan to the handle's coords and
        // teleport the chart. Only consume the dragend that's actually the
        // Stage's own pan-end.
        if (e.target !== e.target.getStage()) return
        setPanX(e.target.x())
        setPanY(e.target.y())
      }}
      onWheel={handleStageWheel}
      onMouseMove={handleStageMouseMove}
      onMouseLeave={handleStageMouseLeave}
      onClick={handleStageClick}
      onContextMenu={handleStageContextMenu}
    >
      <Layer>
        {img && (
          <KImage
            image={img}
            x={imageX}
            y={imageY}
            width={imageDrawW}
            height={imageDrawH}
            listening={false}
          />
        )}

        {/* Activity wedges (WPF TachographChartActivityItem PathGeometry).
            Default opacity 0.85 — solid fill so colors read like WPF, with the
            scanned chart still showing through faintly. Hover/select boosts to 1.0
            and adds a dark stroke so the highlighted wedge stands out without
            requiring others to dim (matches WPF IsHighlighted converter, which only
            adjusts the *highlighted* item's own opacity).
            When the distance-points toggle is ON, hide the wedges entirely so the
            speed-trace ring + driving ticks aren't obscured. WPF does the same:
            the activity layer's Visibility binds to the inverse of the distance
            visibility flag. */}
        {!showDistance && minuteActivities.flatMap((a, idx) => {
          // Wrap-aware sweep: the cross-midnight wrap-merged Rest has
          // `endMin < startMin` (e.g. starts 23:24, ends 04:23). Treat that
          // as +24h so the wedge sweeps through the 12-o'clock midnight
          // position instead of being silently skipped.
          let span = a.endMin - a.startMin
          if (span < 0) span += MINUTES_PER_DAY
          if (span <= 0) return []
          const isHover = hoveredActivityId === a.activityId
          const isSelected = selectedActivityIds?.has(a.activityId) ?? false
          const opacity = isHover || isSelected ? 0.8 : 0.6
          const baseColor = ACTIVITY_COLOR[a.type] ?? '#cbd5e1'
          const grayColor = ACTIVITY_COLOR_GRAY[a.type] ?? '#cbd5e1'

          const onMouseEnter = () => {
            onHoverActivity?.(a.activityId, true)
            setStageCursor('pointer')
          }
          const onMouseLeave = () => {
            onHoverActivity?.(null, true)
            setStageCursor('default')
          }
          const onWedgeClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
            onSelectActivity?.(
              [a.activityId],
              e.evt.shiftKey,
              e.evt.ctrlKey || e.evt.metaKey,
            )
          }
          const onWedgeContext = (e: Konva.KonvaEventObject<PointerEvent>) => {
            e.evt.preventDefault()
            // Stop the contextmenu from bubbling to the Stage handler below, so
            // we don't open the menu twice for a wedge right-click.
            e.cancelBubble = true
            const real = activities.find((x) => x.activityId === a.activityId)
            if (!real) return
            const pos = e.target.getStage()?.getPointerPosition()
            const minute = pos ? Math.round(pointerToMinute(pos.x, pos.y)) : 0
            onContextMenuActivity?.(real, { x: e.evt.clientX, y: e.evt.clientY }, minute)
          }

          // 1:1 with WPF TachographChartActivityItem: when an activity covers
          // the entire 24h period (IsDaylongActivity), render as a filled
          // Ellipse rather than a Wedge (a 360° wedge has a hairline seam at
          // 12 o'clock). A daylong activity is by definition entirely in-day.
          if (span >= MINUTES_PER_DAY) {
            return [
              <Circle
                key={`actfull-${idx}-${a.activityId}`}
                x={cx}
                y={cy}
                radius={outerR}
                fill={baseColor}
                opacity={opacity}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={onWedgeClick}
                onContextMenu={onWedgeContext}
              />,
            ]
          }
          // 1:1 with WPF TachographChart.RefreshBoundaries → CutActivity at
          // dayStart and dayEnd: we visually split the wedge so the boundary
          // line cleanly separates colored (in-day) from gray (out-of-day).
          // splitActivityByDayWindow returns 1-2 sub-ranges per activity.
          const slices = outOfDayGrey
            ? splitActivityByDayWindow(a.startMin, span, startBoundaryMinute, endBoundaryMinute)
            : [{ startMin: a.startMin, spanMin: span, inDay: true }]
          return slices.map((slc, sIdx) => {
            const startDeg = (slc.startMin / MINUTES_PER_DAY) * 360 + 180
            const sweepDeg = (slc.spanMin / MINUTES_PER_DAY) * 360
            return (
              <Wedge
                key={`act-${idx}-${a.activityId}-${slc.startMin}-${sIdx}`}
                x={cx}
                y={cy}
                radius={outerR}
                angle={sweepDeg}
                rotation={startDeg}
                fill={slc.inDay ? baseColor : grayColor}
                opacity={opacity}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={onWedgeClick}
                onContextMenu={onWedgeContext}
              />
            )
          })
        })}

        {/* 24-hour clock face: spokes + hour labels (WPF TachographChartHour).
            Subtract noonShiftMinutes from each hour's angular position so
            dragging the noon-shift handle visibly rotates the time-marking
            ring around the stationary activity wedges. 1:1 with WPF
            TachographChartHour.RefreshHourPoint:151
              `(_hour - _noonShift.TotalHours) * 2π/24`. */}
        <Group listening={false}>
          {Array.from({ length: 24 }).map((_, h) => {
            const a = minuteToAngleRad(h * 60 - noonShiftMinutes)
            return (
              <Line
                key={`spoke-${h}`}
                points={[
                  cx + (outerR - 14) * Math.cos(a),
                  cy + (outerR - 14) * Math.sin(a),
                  cx + (outerR + 14) * Math.cos(a),
                  cy + (outerR + 14) * Math.sin(a),
                ]}
                stroke="#0f172a"
                strokeWidth={h % 6 === 0 ? 2 : 1}
              />
            )
          })}
          {Array.from({ length: 24 }).map((_, h) => {
            const a = minuteToAngleRad(h * 60 - noonShiftMinutes)
            const r = outerR + 26
            return (
              <Text
                key={`hour-${h}`}
                text={String(h)}
                x={cx + r * Math.cos(a) - 7}
                y={cy + r * Math.sin(a) - 8}
                fontSize={13}
                fontStyle="bold"
                fill="#0f172a"
              />
            )
          })}
          <Circle x={cx} y={cy} radius={outerR} stroke="#0f172a" strokeWidth={1} />
        </Group>

        {/* Endpoint dots — drawn on the highlighted activity's start + end angles,
            on the outer ring. Mirrors WPF IsHighlighted-only Ellipses (Resources/
            TachographChart.xaml:87-94, 135-142). */}
        {minuteActivities
          .filter((a) => hoveredActivityId === a.activityId || selectedActivityIds?.has(a.activityId))
          .map((a) => {
            const sa = minuteToAngleRad(a.startMin)
            const ea = minuteToAngleRad(a.endMin)
            return (
              <Group key={`endpoints-${a.activityId}-${a.startMin}`} listening={false}>
                <Circle
                  x={cx + outerR * Math.cos(sa)}
                  y={cy + outerR * Math.sin(sa)}
                  radius={5}
                  fill={ACTIVITY_COLOR[a.type] ?? '#cbd5e1'}
                  stroke="#0f172a"
                  strokeWidth={1}
                />
                <Circle
                  x={cx + outerR * Math.cos(ea)}
                  y={cy + outerR * Math.sin(ea)}
                  radius={5}
                  fill={ACTIVITY_COLOR[a.type] ?? '#cbd5e1'}
                  stroke="#0f172a"
                  strokeWidth={1}
                />
              </Group>
            )
          })}

        <ActivityBoundaryHandles
          activities={activities}
          cx={cx}
          cy={cy}
          radius={outerR}
          hoveredActivityId={hoveredActivityId}
          selectedActivityIds={selectedActivityIds}
          onActivitiesChange={onActivitiesChange}
          onHoverActivity={onHoverActivity}
          setCursor={setStageCursor}
        />

        {/* WPF positions the boundary handles on the OUTER hour-labels ring (not on
            the activity-wedge edge). Match that — sit them ~14 px past the rim, which
            is the same offset as the hour numbers. */}
        {startBoundaryMinute != null && (
          <BoundaryMark
            cx={cx}
            cy={cy}
            handleR={outerR + 14}
            minute={startBoundaryMinute}
            color="#22c55e"
            onChange={onChangeStartBoundary}
            countryCode={beginCountryCode}
          />
        )}
        {endBoundaryMinute != null && (
          <BoundaryMark
            cx={cx}
            cy={cy}
            handleR={outerR + 14}
            minute={endBoundaryMinute}
            color="#ef4444"
            onChange={onChangeEndBoundary}
            countryCode={endCountryCode}
          />
        )}
        {/* Noon-shift handle (black) — 1:1 with WPF TachographChartNoonShift
            .RefreshNoonShiftPoint:119:
              `angle = (-noonShift_hours + 12) * 2π/24`
            i.e. the handle sits where the rotating "12" label currently is on
            the chart. With our minute axis (minute 0 at LEFT, clockwise), that
            means the handle's minute = 12*60 - noonShiftMinutes.
            When the user drags, the new noonShift is recovered as
              new_noonShift = 12*60 - new_minute
            so dragging clockwise (later "noon") increases noonShift, dragging
            counter-clockwise decreases it — matching WPF (drag the marker to
            where you want noon to be displayed). */}
        {onChangeNoonShift && (
          <BoundaryMark
            cx={cx}
            cy={cy}
            handleR={outerR + 14}
            minute={((12 * 60 - noonShiftMinutes) % MINUTES_PER_DAY + MINUTES_PER_DAY) % MINUTES_PER_DAY}
            color="#0f172a"
            onChange={(m) => onChangeNoonShift(12 * 60 - m)}
          />
        )}

        {/* Country-change markers — WPF TachographChartCountryChange: purple radial
            + ISO code label OUTSIDE the rim. */}
        {countryChanges.map((cc, idx) => {
          const a = minuteToAngleRad(cc.minute)
          const labelR = outerR + 26
          return (
            <Group key={`cc-${idx}-${cc.minute}`} listening={false}>
              <Line
                points={[cx, cy, cx + outerR * Math.cos(a), cy + outerR * Math.sin(a)]}
                stroke="#a855f7"
                strokeWidth={2}
                opacity={0.7}
              />
              <Text
                text={cc.code}
                x={cx + labelR * Math.cos(a) - 9}
                y={cy + labelR * Math.sin(a) - 7}
                fontSize={11}
                fontStyle="bold"
                fill="#7e22ce"
              />
            </Group>
          )
        })}

        {/* Vehicle-change markers — WPF TachographChartVehicleChange: thick white
            line from center to rim at each change instant. Drawn on top of wedges
            so the user can see where the vehicle handover happened. */}
        {vehicleChangeMinutes.map((m, idx) => {
          const a = minuteToAngleRad(m)
          return (
            <Line
              key={`vc-${idx}-${m}`}
              points={[cx, cy, cx + outerR * Math.cos(a), cy + outerR * Math.sin(a)]}
              stroke="#ffffff"
              strokeWidth={3}
              shadowColor="#0f172a"
              shadowBlur={2}
              shadowOpacity={0.4}
              listening={false}
            />
          )
        })}

        {/* Phase 3 — speed-trace ring + distance ticks (WPF IsDistancePointsVisible).
            Points are in canonical 800x800 space, transformed via imageScale + the
            same (cx,cy) offset the disc image uses. When onRemoveDistancePoint is
            provided the dots become clickable (left-click removes, 1:1 with WPF
            TachographChartDistancePointItemControl); the dashed speed-trace ring
            stays non-listening so shift+clicks on it still reach the stage's
            add-point handler. */}
        {showDistance && distanceData && (
          <Group>
            <Circle
              x={cx + (distanceData.circleCenterX - CANONICAL_CENTER) * imageScale}
              y={cy + (distanceData.circleCenterY - CANONICAL_CENTER) * imageScale}
              radius={distanceData.circleRadius * imageScale}
              stroke="#0ea5e9"
              strokeWidth={1.2}
              dash={[4, 3]}
              listening={false}
            />
            {distanceData.startPoints.map((p, i) => (
              <Circle
                key={`ds-${i}`}
                x={cx + (p.x - CANONICAL_CENTER) * imageScale}
                y={cy + (p.y - CANONICAL_CENTER) * imageScale}
                radius={4 * imageScale}
                fill="#dc2626"
                listening={!!onRemoveDistancePoint}
                hitStrokeWidth={12}
                onMouseEnter={() => setStageCursor('pointer')}
                onMouseLeave={() => setStageCursor('default')}
                onClick={(e) => {
                  e.cancelBubble = true
                  onRemoveDistancePoint?.('start', i)
                }}
              />
            ))}
            {distanceData.endPoints.map((p, i) => (
              <Circle
                key={`de-${i}`}
                x={cx + (p.x - CANONICAL_CENTER) * imageScale}
                y={cy + (p.y - CANONICAL_CENTER) * imageScale}
                radius={4 * imageScale}
                fill="#0ea5e9"
                listening={!!onRemoveDistancePoint}
                hitStrokeWidth={12}
                onMouseEnter={() => setStageCursor('pointer')}
                onMouseLeave={() => setStageCursor('default')}
                onClick={(e) => {
                  e.cancelBubble = true
                  onRemoveDistancePoint?.('end', i)
                }}
              />
            ))}
          </Group>
        )}

        {/* Legend — pinned to the top-left corner of the canvas, but only when
            there's enough room (the disc would otherwise overlap it on small
            canvases). */}
        {width > 600 && (
          <Group x={8} y={8}>
            <Rect x={-4} y={-4} width={108} height={5 * 18 + 8} fill="rgba(255,255,255,0.92)" stroke="#cbd5e1" strokeWidth={1} cornerRadius={4} />
            {Object.entries(ACTIVITY_LABEL).map(([k, v], i) => (
              <Group key={k} y={i * 18}>
                <Circle radius={5} x={6} y={6} fill={ACTIVITY_COLOR[Number(k)]} />
                <Text x={18} y={1} text={v} fontSize={11} fill="#0f172a" />
              </Group>
            ))}
          </Group>
        )}
      </Layer>
    </Stage>
    {/* Hover tooltip — Konva has no native tooltips; we overlay an absolutely-
        positioned HTML node anchored slightly down-right of the cursor. WPF
        renders the equivalent via DataGridRow ToolTip (XAML:96-112). */}
    {hoveredActivity && tooltipPos && (
      <div
        className="tw-pointer-events-none tw-absolute tw-z-[5] tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-px-2 tw-py-1 tw-text-xs tw-shadow-md"
        style={{ left: tooltipPos.x + 14, top: tooltipPos.y + 14 }}
      >
        <div className="tw-font-semibold tw-text-slate-800">
          {ACTIVITY_LABEL[hoveredActivity.type] ?? '?'}
        </div>
        <div className="tw-font-mono tw-text-slate-600">
          {hoverTimeRange(hoveredActivity, chartAnchorIso, noonShiftMinutes)}
        </div>
      </div>
    )}
    </div>
  )
}

function hoverTimeRange(
  a: ChartScanActivity,
  dayStartIso: string,
  noonShiftMinutes: number,
): string {
  const fmt = (iso: string) => {
    const m = isoToMinute(dayStartIso, iso) + noonShiftMinutes
    const wrap = ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
    const h = Math.floor(wrap / 60)
    const mm = wrap - h * 60
    return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
  }
  // Wrap-merged: end < start in calendar terms, treat as +24h.
  let ms = +new Date(a.endTime) - +new Date(a.startTime)
  if (ms < 0) ms += 24 * 60 * 60 * 1000
  const dMin = Math.round(ms / 60_000)
  const dh = Math.floor(dMin / 60)
  const dm = dMin - dh * 60
  return `${fmt(a.startTime)} – ${fmt(a.endTime)} · ${String(dh).padStart(2, '0')}:${String(dm).padStart(2, '0')}`
}

// Test whether an activity's [startMin, endMin) span sits inside the day window
// [dayStart, dayEnd). Returns true when the activity overlaps the window at all
// (matches WPF IsOutOfDay = true only when the activity is entirely after the
// EndBoundary; partial overlap counts as in-day).
function isInsideDay(
  startMin: number,
  endMin: number,
  dayStart: number | null | undefined,
  dayEnd: number | null | undefined,
): boolean {
  if (dayStart == null || dayEnd == null) return true
  if (dayEnd >= dayStart) {
    return endMin > dayStart && startMin < dayEnd
  }
  // Wrap window: [dayStart, MINUTES_PER_DAY) ∪ [0, dayEnd)
  return endMin > dayStart || startMin < dayEnd
}

// 1:1 with WPF TachographChart.RefreshBoundaries (TachographChart.cs:856-873):
// when boundaries change WPF cuts activities at the boundary positions so each
// resulting activity is fully inside or fully outside the day window. We don't
// mutate the activity data, but we can do the equivalent visually by splitting
// the wedge geometry: given an activity span [aStartMin, aStartMin+aSpanMin]
// (aSpanMin in (0, 1440], wrap-aware), return one or two sub-ranges tagged
// `inDay`/`outOfDay`. Each sub-range is then rendered as its own Konva.Wedge,
// so when a boundary line falls mid-activity the colored portion ends exactly
// at the boundary instead of bleeding past it.
type WedgeSlice = { startMin: number; spanMin: number; inDay: boolean }
function splitActivityByDayWindow(
  aStartMin: number,
  aSpanMin: number,
  dayStart: number | null | undefined,
  dayEnd: number | null | undefined,
): WedgeSlice[] {
  if (dayStart == null || dayEnd == null) {
    return [{ startMin: aStartMin, spanMin: aSpanMin, inDay: true }]
  }
  // Walk the activity arc minute-by-minute on the 24h ring, classify each
  // minute as in/out, and emit contiguous runs. This is O(span) but span is
  // ≤ 1440 — cheap for the dozens of wedges we render.
  const M = MINUTES_PER_DAY
  const inWindow = (minute: number) => {
    const m = ((minute % M) + M) % M
    if (dayEnd >= dayStart) return m >= dayStart && m < dayEnd
    return m >= dayStart || m < dayEnd
  }
  const out: WedgeSlice[] = []
  let runStart = 0
  let runIn = inWindow(aStartMin)
  for (let i = 1; i <= aSpanMin; i++) {
    const inHere = i < aSpanMin ? inWindow(aStartMin + i) : !runIn
    if (inHere !== runIn) {
      out.push({
        startMin: ((aStartMin + runStart) % M + M) % M,
        spanMin: i - runStart,
        inDay: runIn,
      })
      runStart = i
      runIn = inHere
    }
  }
  return out
}

function BoundaryMark({
  cx,
  cy,
  handleR,
  minute,
  color,
  onChange,
  countryCode,
}: {
  cx: number
  cy: number
  handleR: number
  minute: number
  color: string
  onChange?: (minute: number) => void
  // ISO code rendered next to the boundary handle (WPF
  // TachographChartBoundaryItem renders Country.Code as a TextBlock at the
  // dot's position).
  countryCode?: string
}) {
  const angle = minuteToAngleRad(minute)
  const px = cx + handleR * Math.cos(angle)
  const py = cy + handleR * Math.sin(angle)
  // Hover/drag affordance: WPF uses BoundaryColor + IsHover Opacity converter
  // (TachographChartBoundaryItem template). We bump radius + drop a soft shadow
  // so the user knows the dot is grabbable.
  const [hover, setHover] = React.useState(false)
  const r = hover ? 9 : 7
  return (
    <Group>
      <Line
        points={[cx, cy, px, py]}
        stroke={color}
        strokeWidth={2}
        opacity={hover ? 0.9 : 0.5}
        listening={false}
      />
      {countryCode && (
        <Text
          // Offset the label slightly outside the dot, away from the disc center.
          x={cx + (handleR + 14) * Math.cos(angle) - 9}
          y={cy + (handleR + 14) * Math.sin(angle) - 7}
          text={countryCode}
          fontSize={11}
          fontStyle="bold"
          fill={color}
          listening={false}
        />
      )}
      <Circle
        x={px}
        y={py}
        radius={r}
        fill={color}
        stroke="#ffffff"
        strokeWidth={2}
        shadowColor={color}
        shadowBlur={hover ? 8 : 0}
        shadowOpacity={hover ? 0.7 : 0}
        draggable={!!onChange}
        // Stop the pointer-down/drag-start from bubbling to the Stage so
        // panning (when zoom > 1) doesn't kick in alongside the handle drag.
        onMouseDown={(e) => { if (onChange) e.cancelBubble = true }}
        onMouseEnter={(e) => {
          setHover(true)
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = onChange ? 'grab' : 'default'
        }}
        onMouseLeave={(e) => {
          setHover(false)
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = 'default'
        }}
        onDragStart={(e) => {
          e.cancelBubble = true
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = 'grabbing'
        }}
        onDragEnd={(e) => {
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = 'default'
        }}
        onDragMove={(e) => {
          const node = e.target
          const dx = node.x() - cx
          const dy = node.y() - cy
          const a = Math.atan2(dy, dx)
          let m = ((a - Math.PI) * MINUTES_PER_DAY) / (2 * Math.PI)
          m = ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
          onChange?.(Math.round(m))
          node.position({
            x: cx + handleR * Math.cos(a),
            y: cy + handleR * Math.sin(a),
          })
        }}
      />
    </Group>
  )
}
